import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GitArtifactType, GitContextProvider } from "git";
import { getWorkspaceIdForImport } from "ee/selectors/applicationSelectors";
import {
  hasGitPkgConnectPermission,
  hasGitPkgManageDefaultBranchPermission,
} from "ee/utils/permissionHelpers";
import {
  fetchAllApplicationsOfWorkspace,
  setWorkspaceIdForImport,
} from "ee/actions/applicationActions";
import { getCurrentAppWorkspace } from "ee/selectors/selectedWorkspaceSelectors";
import {
  getCurrentPackage,
  getPackagesList,
} from "ee/selectors/packageSelectors";
import type { Package } from "ee/constants/PackageConstants";
import packageStatusTransformer from "../packageStatusTransformer";
import { selectGitPackageEnabled } from "ee/selectors/moduleFeatureSelectors";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import { fetchSSHKeysInit } from "ee/actions/sshKeysActions";
import {
  selectSSHKeysList,
  selectSSHKeysLoading,
} from "ee/selectors/sshKeysSelectors";
import { adminSettingsCategoryUrl } from "ee/RouteBuilder";
import { SettingCategories } from "ee/pages/AdminSettings/config/types";
import history from "utils/history";

interface FlaggedGitPackageContextProviderProps {
  children: JSX.Element;
}

function FlaggedGitPackageContextProvider({
  children,
}: FlaggedGitPackageContextProviderProps) {
  const dispatch = useDispatch();

  const artifactType = GitArtifactType.Package;
  const artifact = useSelector(getCurrentPackage);
  const artifacts = useSelector(getPackagesList) as Package[];
  const workspace = useSelector(getCurrentAppWorkspace);
  const importWorkspaceId = useSelector(getWorkspaceIdForImport);
  const isConnectPermitted = hasGitPkgConnectPermission(
    artifact?.userPermissions ?? [],
  );

  const setImportWorkspaceId = useCallback(() => {
    dispatch(
      setWorkspaceIdForImport({ editorId: "", workspaceId: workspace.id }),
    );
  }, [dispatch, workspace.id]);

  const fetchApplications = useCallback(() => {
    dispatch(fetchAllApplicationsOfWorkspace());
  }, [dispatch]);

  const isManageDefaultBranchPermitted = useMemo(() => {
    return hasGitPkgManageDefaultBranchPermission(
      artifact?.userPermissions ?? [],
    );
  }, [artifact]);

  // SSH key manager
  const isSSHKeyManagerEnabled = useFeatureFlag(
    FEATURE_FLAG.release_ssh_key_manager_enabled,
  );
  const sshKeys = useSelector(selectSSHKeysList);
  const isSSHKeysLoading = useSelector(selectSSHKeysLoading);

  const fetchSSHKeys = useCallback(() => {
    dispatch(fetchSSHKeysInit());
  }, [dispatch]);

  const onCreateSSHKey = useCallback(() => {
    history.push(
      adminSettingsCategoryUrl({ category: SettingCategories.SSH_KEYS }),
    );
  }, []);

  return (
    <GitContextProvider
      artifact={artifact ?? null}
      artifactType={artifactType}
      artifacts={artifacts ?? null}
      baseArtifactId={artifact?.baseId ?? ""}
      fetchArtifacts={fetchApplications}
      fetchSSHKeys={fetchSSHKeys}
      importWorkspaceId={importWorkspaceId}
      isConnectPermitted={isConnectPermitted}
      isManageAutocommitPermitted={false}
      isManageDefaultBranchPermitted={isManageDefaultBranchPermitted}
      isManageProtectedBranchesPermitted={false}
      isSSHKeyManagerEnabled={isSSHKeyManagerEnabled}
      isSSHKeysLoading={isSSHKeysLoading}
      onCreateSSHKey={onCreateSSHKey}
      setImportWorkspaceId={setImportWorkspaceId}
      sshKeys={sshKeys}
      statusTransformer={packageStatusTransformer}
      workspace={workspace ?? null}
    >
      {children}
    </GitContextProvider>
  );
}

interface GitPackageContextProviderProps {
  children: JSX.Element;
}

export default function GitPackageContextProvider({
  children,
}: GitPackageContextProviderProps) {
  const isGitPackageEnabled = useSelector(selectGitPackageEnabled);

  if (!isGitPackageEnabled) {
    return children;
  }

  return (
    <FlaggedGitPackageContextProvider>
      {children}
    </FlaggedGitPackageContextProvider>
  );
}
